/* eslint-disable max-lines */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useGlobal } from '../../contexts/GlobalContext'
import { obterHomeBookster } from '../../api/home.servico'
import { solicitaCancelamento, desfazSolicitacaoCancelamento } from '../../api/cancelamento.servico'
import { postRenovacaoPlanoAPI } from '../../api/renovacao.servico'
import { postReativarAssinaturaAPI } from '../../api/reativacao.servico'
import TopBar from './components/TopBar/TopBar'
import Passport from './components/Passport/Passport'
import Comunidade from './components/Comunidade/Comunidade'
import Footer from './components/Footer/Footer'
import Toasts from './components/Toasts/Toasts'
import RenewFlow from './components/Modals/RenewFlow'
import ReactivateFlow from './components/Modals/ReactivateFlow'
import UndoCancelFlow from './components/Modals/UndoCancelFlow'
import PaymentFlow from './components/Modals/PaymentFlow'
import AddressManager from './components/Modals/AddressManager'
import CancelFlow from './components/Modals/CancelFlow'
import { BooksterGlobalStyle } from './styles'
import {
  emitirEventoCancelamento,
  emitirEventoDesfazerCancelamento,
  emitirEventosCompra
} from './data/bookster-events'
import {
  Scope,
  Shell,
  Hero,
  Message,
  Spinner
} from './layout-styles'
import {
  tenureText,
  fmtLong,
  fmtMesAno,
  fmtRenewFull,
  derivarCards,
  derivarAddrs,
  derivarPay,
  derivarProximaEdicao,
  derivarSituacaoCancelamento,
  temEstornoNoCancelamento
} from './data/assinatura-utils'

const TIPO_PAGAMENTO_CARTAO = 'CARTAO_CREDITO'

export default function Bookster() {
  const { isAssociadoLogado, fazLogout } = useGlobal()
  const [ estado, setEstado ] = useState( { 'status': 'loading', 'assinatura': null, 'associado': null } )
  const [ modal, setModal ] = useState( null )
  const [ toasts, setToasts ] = useState( [] )
  const [ cards, setCards ] = useState( [] )
  const [ addrs, setAddrs ] = useState( [] )
  const [ pay, setPay ] = useState( 'card' )

  const toast = useCallback( ( msg ) => {
    const id = Math.random().toString( 36 )
      .slice( 2 )
    setToasts( ( prev ) => [ ...prev, { id, msg } ] )
    setTimeout( () => {
      setToasts( ( prev ) => prev.filter( ( item ) => item.id !== id ) )
    }, 3400 )
  }, [] )

  const deveRecarregar = useRef( false )

  const recarregar = useCallback( async() => {
    try {
      const resp = await obterHomeBookster()
      const dados = resp?.data || {}
      const assinatura = dados.assinatura || null
      setEstado( {
        'status': assinatura ? 'ok' : 'sem-assinatura',
        assinatura,
        'associado': dados.associado || null,
      } )
      setCards( derivarCards( assinatura ) )
      setAddrs( derivarAddrs( assinatura ) )
      setPay( derivarPay( assinatura ) )
    } catch ( erro ) {
      // refresh silencioso: mantém os dados atuais em caso de falha
    }
  }, [] )

  const fecharModal = useCallback( () => {
    setModal( null )
    if ( deveRecarregar.current ) {
      deveRecarregar.current = false
      recarregar()
    }
  }, [ recarregar ] )

  useEffect( () => {
    let cancelado = false

    async function carregar() {
      if ( !isAssociadoLogado ) {
        return
      }

      setEstado( { 'status': 'loading', 'assinatura': null, 'associado': null } )

      try {
        const resp = await obterHomeBookster()

        if ( cancelado ) {
          return
        }

        const dados = resp?.data || {}
        const assinatura = dados.assinatura || null

        setEstado( {
          'status': assinatura ? 'ok' : 'sem-assinatura',
          assinatura,
          'associado': dados.associado || null,
        } )
        setCards( derivarCards( assinatura ) )
        setAddrs( derivarAddrs( assinatura ) )
        setPay( derivarPay( assinatura ) )
      } catch ( erro ) {
        if ( cancelado ) {
          return
        }
        const code = erro?.response?.status
        setEstado( {
          'status': code === 404 ? 'sem-assinatura' : 'erro',
          'assinatura': null,
          'associado': null,
        } )
      }
    }

    carregar()
    return () => {
      cancelado = true
    }
  }, [ isAssociadoLogado ] )

  const dataRenovacaoVisivel = estado.assinatura?.podeRenovar === false
    ? null
    : estado.assinatura?.dataProximaRenovacaoAutomatica

  const cancelFlowSub = useMemo( () => ( {
    'renewLong': fmtLong( dataRenovacaoVisivel ),
    'tenureText': tenureText( estado.assinatura?.ativoDesde ),
    'proximaEdicao': derivarProximaEdicao( estado.assinatura ),
    'edicaoAtual': estado.assinatura?.edicaoBookster || null,
    'comEstorno': temEstornoNoCancelamento( estado.assinatura ),
    'fimVigencia': fmtMesAno( dataRenovacaoVisivel ),
  } ), [ estado.assinatura, dataRenovacaoVisivel ] )

  const renewFull = fmtRenewFull( dataRenovacaoVisivel )
  const cardPrincipal = cards[ 0 ] || null
  const addrPrincipal = addrs[ 0 ] || null

  const renovar = useCallback( async( payload ) => {
    const { data } = await postRenovacaoPlanoAPI( payload )
    deveRecarregar.current = true
    if ( payload?.pagamento?.tipo === TIPO_PAGAMENTO_CARTAO ) {
      emitirEventosCompra( {
        'tipo': 'renovacao',
        data,
        'assinatura': estado.assinatura,
        'associado': estado.associado,
        'metodoPagamento': payload.pagamento.tipo
      } )
    }
    return data
  }, [ estado.assinatura, estado.associado ] )

  const reativar = useCallback( async( payload ) => {
    const { data } = await postReativarAssinaturaAPI( payload )
    deveRecarregar.current = true
    if ( payload?.pagamento?.tipo === TIPO_PAGAMENTO_CARTAO ) {
      emitirEventosCompra( {
        'tipo': 'reativacao',
        data,
        'assinatura': estado.assinatura,
        'associado': estado.associado,
        'metodoPagamento': payload.pagamento.tipo
      } )
    }
    return data
  }, [ estado.assinatura, estado.associado ] )

  const confirmarCancelamento = useCallback( async( motivos, origem ) => {
    const idAssinatura = estado.assinatura?.id
    if ( !idAssinatura ) {
      throw new Error( 'Assinatura sem identificador' )
    }
    await solicitaCancelamento( idAssinatura, motivos )
    emitirEventoCancelamento( { 'assinatura': estado.assinatura, motivos, origem } )
    await recarregar()
  }, [ estado.assinatura, recarregar ] )

  const desfazerCancelamento = useCallback( async() => {
    const idAssinatura = estado.assinatura?.id
    if ( !idAssinatura ) {
      throw new Error( 'Assinatura sem identificador' )
    }
    await desfazSolicitacaoCancelamento( idAssinatura )
    emitirEventoDesfazerCancelamento( { 'assinatura': estado.assinatura } )
    await recarregar()
  }, [ estado.assinatura, recarregar ] )

  const handleCartaoVigenteChange = useCallback( ( novoCartao ) => {
    setEstado( ( prev ) => (
      prev.assinatura
        ? {
          ...prev,
          'assinatura': {
            ...prev.assinatura,
            'pagamento': {
              ...prev.assinatura.pagamento,
              'cartaoCredito': {
                'id': novoCartao.id,
                'bandeira': novoCartao.brand,
                'ultimosDigitos': novoCartao.last4,
                'nome': novoCartao.name,
                'validade': novoCartao.exp,
              }
            }
          }
        }
        : prev
    ) )
  }, [] )

  return (
    <>
      <BooksterGlobalStyle />
      <Scope className="bookster-scope">
        <TopBar
          active="assinatura"
          nome={ estado.associado?.nome }
        />
        <Shell>
          <Hero>
            { estado.status === 'loading' ? <Spinner>Carregando sua jornada…</Spinner> : null }
            { estado.status === 'sem-assinatura' ? (
              <Message>Não encontramos uma assinatura Bookster ativa para o seu perfil.</Message>
            ) : null }
            { estado.status === 'erro' ? (
              <Message>Não foi possível carregar sua assinatura agora. Tente novamente em instantes.</Message>
            ) : null }
            { estado.status === 'ok' && estado.assinatura ? (
              <Passport
                addrPrincipal={ addrPrincipal }
                assinatura={ estado.assinatura }
                associado={ estado.associado }
                cardPrincipal={ cardPrincipal }
                formaPagamento={ pay }
                nomeAssinatura="Assinatura Bookster"
                onAddressClick={ () => setModal( 'addr' ) }
                onCancelClick={ () => setModal( 'cancel' ) }
                onPaymentClick={ () => setModal( 'pay' ) }
                onReactivateClick={ () => setModal( 'reactivate' ) }
                onRenewClick={ () => setModal( 'renew' ) }
                onUndoClick={ () => setModal( 'undo' ) }
                precoRenovacao={ estado.assinatura?.precoRenovacao }
                totalAddrs={ addrs.length }
              />
            ) : null }
          </Hero>
          { estado.status === 'ok' && estado.assinatura ? <Comunidade /> : null }
        </Shell>
        <Footer onLogout={ fazLogout } />

        { modal === 'renew' ? (
          <RenewFlow
            card={ cardPrincipal }
            idAssinatura={ estado.assinatura?.id }
            metodo={ pay }
            onClose={ fecharModal }
            onRenew={ renovar }
            pixPendente={ estado.assinatura?.pixPendente || null }
            podeRenovar={ estado.assinatura?.podeRenovar !== false }
            precoRenovacao={ estado.assinatura?.precoRenovacao }
            renewFull={ renewFull }
          />
        ) : null }
        { modal === 'reactivate' ? (
          <ReactivateFlow
            card={ cardPrincipal }
            enderecoId={ addrPrincipal?.id }
            idAssinatura={ estado.assinatura?.id }
            metodo={ pay }
            onClose={ fecharModal }
            onReactivate={ reativar }
            pixPendente={ estado.assinatura?.pixPendente || null }
            planoId={ estado.assinatura?.planoId }
            precoRenovacao={ estado.assinatura?.precoRenovacao }
          />
        ) : null }
        { modal === 'undo' ? (
          <UndoCancelFlow
            edicaoRenovacao={ cancelFlowSub.proximaEdicao }
            onClose={ fecharModal }
            onRenew={ () => setModal( 'renew' ) }
            onUndo={ desfazerCancelamento }
            podeRenovar={ estado.assinatura?.podeRenovar !== false }
          />
        ) : null }
        { modal === 'pay' ? (
          <PaymentFlow
            assinaturaId={ estado.assinatura?.id }
            cardsInitial={ cards }
            cartaoVigenteId={ estado.assinatura?.pagamento?.cartaoCredito?.id }
            onCards={ setCards }
            onCartaoVigenteChange={ handleCartaoVigenteChange }
            onClose={ () => setModal( null ) }
            onPay={ setPay }
            pay={ pay }
            toast={ toast }
          />
        ) : null }
        { modal === 'addr' ? (
          <AddressManager
            addrsInitial={ addrs }
            assinaturaId={ estado.assinatura?.id }
            onAddrs={ setAddrs }
            onClose={ () => setModal( null ) }
            onEnderecoVigenteChange={ ( novoEndereco ) => {
              setEstado( ( prev ) => (
                prev.assinatura
                  ? {
                    ...prev,
                    'assinatura': {
                      ...prev.assinatura,
                      'entrega': { 'endereco': novoEndereco }
                    }
                  }
                  : prev
              ) )
            } }
            toast={ toast }
          />
        ) : null }
        { modal === 'cancel' ? (
          <CancelFlow
            onAddress={ () => setModal( 'addr' ) }
            onClose={ fecharModal }
            onConfirm={ async( motivos, origem ) => {
              await confirmarCancelamento( motivos, origem )
              toast( 'Cancelamento agendado.' )
            } }
            onReactivate={ () => setModal( 'reactivate' ) }
            onUndo={ () => setModal( 'undo' ) }
            situacao={ derivarSituacaoCancelamento( estado.assinatura?.status ) }
            sub={ cancelFlowSub }
          />
        ) : null }

        <Toasts items={ toasts } />
      </Scope>
    </>
  )
}
